// adminportal/src/components/SftpConnectorManager.tsx

import React, { useState, useEffect } from 'react';
import {
  Plus,
  Edit,
  Trash2,
  TestTube,
  Server,
  AlertTriangle,
  FolderOpen,
  Upload,
  Download,
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  RefreshCw,
  Copy,
} from 'lucide-react';
import Button from './Button';
import Card from './Card';
import DualPaneFileBrowser from './DualPaneFileBrowser';
import { apiUrl } from '../config';

interface SftpConnector {
  connectorId: string;
  awsConnectorId?: string; // AWS Transfer Family connector ID
  name: string;
  host: string;
  port: number;
  username: string;
  companyId: string;
  companyName?: string; // Added for display
  workflowType: 'INBOUND' | 'OUTBOUND'; // NEW: Workflow direction
  destinationType: 'INBOUND' | 'OUTBOUND' | 'CUSTOM_PATH'; // NEW: Clearer destination logic
  s3Bucket: string;
  s3Prefix: string;
  authenticationType: 'password' | 'ssh-key';
  hostKey?: string; // Added for host key storage
  tags?: Record<string, string>;
  createdAt: string;
  updatedAt: string;
}

interface SftpConnectorFormData {
  name: string;
  host: string;
  port: number;
  username: string;
  password: string;
  sshPrivateKey: string;
  workflowType: 'INBOUND' | 'OUTBOUND'; // NEW: Workflow direction
  destinationType: 'INBOUND' | 'OUTBOUND' | 'CUSTOM_PATH'; // NEW: Clearer destination logic
  s3Bucket: string;
  s3Prefix: string;
  authenticationType: 'password' | 'ssh-key';
  tags: Record<string, string>;
  enableAutoPush: boolean; // Pattern-A++ auto-push with tag-based routing
  homeDirectoryPath: string; // SFTP home directory path for auto-push
  notificationEmail: string; // Email for success/failure notifications
}

interface SftpConnectorManagerProps {
  companyId?: string;
  companyName?: string;
}

export default function SftpConnectorManager({
  companyId,
  companyName,
}: SftpConnectorManagerProps) {
  const [connectors, setConnectors] = useState<SftpConnector[]>([]);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingConnector, setEditingConnector] = useState<SftpConnector | null>(null);
  const [testingConnection, setTestingConnection] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showFileOperations, setShowFileOperations] = useState<string | null>(null);
  const [showFileBrowser, setShowFileBrowser] = useState<string | null>(null);
  const [directoryListing, setDirectoryListing] = useState<Record<string, any[]>>({});
  const [loadingDirectory, setLoadingDirectory] = useState<string | null>(null);
  const [transferProgress, setTransferProgress] = useState<Record<string, any>>({});
  const [transferHistory, setTransferHistory] = useState<Record<string, any[]>>({});
  const [companies, setCompanies] = useState<Record<string, string>>({});

  const [form, setForm] = useState<SftpConnectorFormData>({
    name: '',
    host: '',
    port: 22,
    username: '',
    password: '',
    sshPrivateKey: '',
    workflowType: 'OUTBOUND',
    destinationType: 'OUTBOUND', // Auto-select based on workflow type
    s3Bucket: 'ohit-dev-transfer-storage',
    s3Prefix: '',
    authenticationType: 'password',
    tags: {},
    enableAutoPush: false, // Pattern-A++ auto-push with tag-based routing
    homeDirectoryPath: 'upload', // Default SFTP remote directory
    notificationEmail: '', // Email for notifications
  });

  useEffect(() => {
    loadConnectors();
    loadCompanies();
  }, [companyId]);

  const loadCompanies = async () => {
    try {
      const response = await fetch(apiUrl('companies'));
      if (response.ok) {
        const data = await response.json();
        const companyMap: Record<string, string> = {};
        data.companies?.forEach((company: any) => {
          companyMap[company.companyId] = company.name;
        });
        setCompanies(companyMap);
      }
    } catch (error) {
      console.error('Failed to load companies:', error);
    }
  };

  const loadConnectors = async () => {
    // Loading connectors for company
    setLoading(true);
    try {
      // Build URL based on whether a company is selected
      const url =
        companyId && companyId !== 'undefined'
          ? apiUrl(`sftp-connectors?companyId=${companyId}`)
          : apiUrl('sftp-connectors'); // Fetch all connectors when no company selected

      // Fetching connectors from API
      const response = await fetch(url);
      // Response status received
      if (!response.ok) throw new Error('Failed to load connectors');
      const data = await response.json();
      // Response data received
      setConnectors(data.connectors || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({
      name: '',
      host: '',
      port: 22,
      username: '',
      password: '',
      sshPrivateKey: '',
      workflowType: 'OUTBOUND',
      destinationType: 'INBOUND', // Default to INBOUND for reset
      s3Bucket: 'ohit-dev-transfer-storage',
      s3Prefix: '',
      authenticationType: 'password',
      tags: {},
      enableAutoPush: false, // Reset auto-push
      homeDirectoryPath: 'upload', // Reset to default
      notificationEmail: '', // Reset email
    });
    setError(null);
    setSuccess(null);
  };

  const handleDestinationTypeChange = (type: 'INBOUND' | 'OUTBOUND' | 'CUSTOM_PATH') => {
    setForm(prev => ({
      ...prev,
      destinationType: type,
      s3Prefix: type === 'CUSTOM_PATH' ? prev.s3Prefix : '',
    }));
  };

  const generateS3Prefix = () => {
    // Convert company name to lowercase and replace spaces with hyphens to match S3 structure
    if (!companyName) return '';
    const normalizedCompanyName = companyName.toLowerCase().replace(/\s+/g, '-');

    switch (form.destinationType) {
      case 'INBOUND':
        return `companies/${normalizedCompanyName}/inbound/`; // Files coming TO company
      case 'OUTBOUND':
        return `companies/${normalizedCompanyName}/outbound/`; // Files going FROM company
      case 'CUSTOM_PATH':
        return form.s3Prefix || ''; // Custom path specified by user
      default:
        return `companies/${normalizedCompanyName}/`;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    // Check if companyId is available
    if (!companyId || companyId === 'undefined') {
      setError('Please select a company before creating a connector');
      setLoading(false);
      return;
    }

    try {
      const payload = {
        ...form,
        host: form.host.trim(), // Trim whitespace from host
        companyId,
        companyName,
        s3Prefix: generateS3Prefix(),
      };

      // Send as URL-encoded string to avoid preflight request
      const formData = new URLSearchParams();
      Object.entries(payload).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          formData.append(key, String(value));
        }
      });

      const response = await fetch(apiUrl('sftp-connectors'), {
        method: 'POST',
        // Send as URL-encoded string without Content-Type header
        body: formData.toString(),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create connector');
      }

      const connectorData = await response.json();
      const connectorId = connectorData.connectorId || connectorData.connector?.connectorId;

      // If auto-push is enabled, add Pattern-A++ routing tags
      if (form.enableAutoPush && connectorId) {
        try {
          console.log('🏷️ Adding Pattern-A++ routing tags to connector:', connectorId);

          const tags = {
            autopush: 'enabled',
            homeDirectoryPath: form.homeDirectoryPath,
            notificationEmail: form.notificationEmail,
          };

          const tagResponse = await fetch(apiUrl(`sftp-connectors/${connectorId}/tags`), {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ tags }),
          });

          if (tagResponse.ok) {
            console.log('✅ Pattern-A++ routing tags added successfully');
            setSuccess(
              'SFTP connector created successfully! Pattern-A++ auto-push enabled with intelligent routing.'
            );
          } else {
            console.warn('⚠️ Failed to add Pattern-A++ routing tags, but connector was created');
            setSuccess(
              'SFTP connector created successfully! Note: Auto-push setup failed - you can configure it manually later.'
            );
          }
        } catch (error) {
          console.warn('Pattern-A++ setup failed:', error);
          setSuccess(
            'SFTP connector created successfully! Note: Auto-push setup failed - you can configure it manually later.'
          );
        }
      } else {
        setSuccess('SFTP connector created successfully!');
      }

      setShowCreateModal(false);
      resetForm();
      loadConnectors();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEditConnector = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingConnector) return;

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const payload = {
        ...form,
        companyId,
        s3Prefix: generateS3Prefix(),
      };

      // Convert payload to FormData to avoid preflight request
      const formData = new FormData();
      Object.entries(payload).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          // Handle objects by converting to JSON string
          if (typeof value === 'object') {
            formData.append(key, JSON.stringify(value));
          } else {
            formData.append(key, String(value));
          }
        }
      });

      const response = await fetch(apiUrl(`sftp-connectors/${editingConnector.connectorId}`), {
        method: 'PUT',
        // Using FormData makes this a simple request (no preflight)
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update connector');
      }

      // Handle Pattern-A++ auto-push changes (tagging only, no workflow creation)
      if (form.enableAutoPush) {
        try {
          console.log(
            '🏷️ Adding Pattern-A++ routing tags to connector:',
            editingConnector.connectorId
          );

          const tags = {
            autopush: 'enabled',
            homeDirectoryPath: form.homeDirectoryPath,
            notificationEmail: form.notificationEmail,
          };

          const tagResponse = await fetch(
            apiUrl(`sftp-connectors/${editingConnector.connectorId}/tags`),
            {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ tags }),
            }
          );

          if (tagResponse.ok) {
            console.log('✅ Pattern-A++ routing tags added successfully');
          } else {
            console.warn('⚠️ Failed to add Pattern-A++ routing tags');
          }
        } catch (error) {
          console.warn('Pattern-A++ setup failed:', error);
        }
      } else {
        // Disable auto-push (remove tags)
        try {
          const tags = {
            autopush: 'disabled',
          };

          const tagResponse = await fetch(
            apiUrl(`sftp-connectors/${editingConnector.connectorId}/tags`),
            {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ tags }),
            }
          );

          if (tagResponse.ok) {
            console.log('✅ Pattern-A++ routing tags removed successfully');
          } else {
            console.warn('⚠️ Failed to remove Pattern-A++ routing tags');
          }
        } catch (error) {
          console.warn('Pattern-A++ disable failed:', error);
        }
      }

      setSuccess('SFTP connector updated successfully!');
      setShowEditModal(false);
      setEditingConnector(null);
      resetForm();
      loadConnectors();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const populateFormForEdit = async (connector: SftpConnector) => {
    // Check if connector has auto-push enabled based on tags
    const hasAutoPush = connector.tags?.autopush === 'enabled';

    setForm({
      name: connector.name,
      host: connector.host,
      port: connector.port,
      username: connector.username,
      password: '', // Don't populate password for security
      sshPrivateKey: '', // Don't populate private key for security
      workflowType: connector.workflowType,
      destinationType: connector.destinationType,
      s3Bucket: connector.s3Bucket,
      s3Prefix: connector.s3Prefix,
      authenticationType: connector.authenticationType,
      tags: connector.tags || {},
      enableAutoPush: hasAutoPush, // Initialize based on existing tags
      homeDirectoryPath: connector.tags?.homeDirectoryPath || 'upload', // Get from tags or default
      notificationEmail: connector.tags?.notificationEmail || '', // Get from tags or empty
    });
    setEditingConnector(connector);
    setShowEditModal(true);
  };

  const handleDelete = async (connectorId: string) => {
    // TODO: Replace with proper confirmation dialog
    // if (!confirm('Are you sure you want to delete this connector?')) return;

    setLoading(true);
    try {
      const response = await fetch(apiUrl(`sftp-connectors/${connectorId}`), {
        method: 'DELETE',
      });

      if (!response.ok) throw new Error('Failed to delete connector');

      setSuccess('Connector deleted successfully!');
      loadConnectors();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const updateConnectorHostKey = async (connectorId: string, hostKey: string) => {
    try {
      const response = await fetch(apiUrl(`sftp-connectors/${connectorId}/host-key`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ hostKey }),
      });

      if (!response.ok) {
        console.error('Failed to save host key');
      } else {
        console.log('Host key saved successfully');
      }
    } catch (error) {
      console.error('Error saving host key:', error);
    }
  };

  const handleTestConnection = async (connectorId: string) => {
    console.log('🚀 Test connection function called for connector:', connectorId);
    setTestingConnection(connectorId);
    try {
      console.log('🌐 Making API call to test connection...');
      const response = await fetch(apiUrl(`sftp-connectors/test-connection`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          connectorId,
          // Include any additional parameters needed for the test
        }),
      });

      console.log('🔍 Response status:', response.status, 'OK:', response.ok);
      const result = await response.json();
      console.log('🔍 Response data:', result);

      if (response.ok && result.success) {
        setSuccess('Connection test successful!');
        // If the test returns a host key, save it to the connector
        if (result.hostKey) {
          console.log('Retrieved host key:', result.hostKey);
          setSuccess('Connection test successful! Host key retrieved and saved.');

          // Update the connector with the host key
          await updateConnectorHostKey(connectorId, result.hostKey);
        }
      } else {
        // Handle both 400 (connection failed) and 200 with success: false
        const errorMessage = result.details || result.error || 'Connection test failed';
        console.log('🔍 Connection test error details:', result);
        console.log('🔍 Error message extracted:', errorMessage);
        const fullErrorMessage = `Connection test failed: ${errorMessage}`;
        console.log('🔍 Full error message to set:', fullErrorMessage);
        setError(fullErrorMessage);
        console.log('🔍 setError called with:', fullErrorMessage);
      }
    } catch (err: any) {
      setError(`Connection test failed: ${err.message}`);
    } finally {
      setTestingConnection(null);
    }
  };

  const handleDirectoryListing = async (connectorId: string, path: string = '/') => {
    setLoadingDirectory(connectorId);
    try {
      const response = await fetch(
        apiUrl(`sftp-connectors/${connectorId}/operations/directory-listing`),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ connectorId, path }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Directory listing failed');
      }

      const result = await response.json();
      setDirectoryListing(prev => ({ ...prev, [connectorId]: result.files || [] }));
      setSuccess(`Directory listing successful for ${path}`);
    } catch (err: any) {
      setError(`Directory listing failed: ${err.message}`);
    } finally {
      setLoadingDirectory(null);
    }
  };

  const handleFileTransfer = async (
    connectorId: string,
    transferType: 'upload' | 'download',
    filePaths: string[]
  ) => {
    try {
      const payload = {
        connectorId,
        localDirectoryPath: '/tmp',
        remoteDirectoryPath: '/',
        ...(transferType === 'download'
          ? { retrieveFilePaths: filePaths }
          : { sendFilePaths: filePaths }),
      };

      const response = await fetch(
        apiUrl(`sftp-connectors/${connectorId}/operations/file-transfer`),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'File transfer failed');
      }

      const result = await response.json();
      setTransferProgress(prev => ({
        ...prev,
        [connectorId]: {
          transferId: result.transferId,
          status: 'IN_PROGRESS',
          startTime: new Date().toISOString(),
          files: filePaths,
        },
      }));
      setSuccess(`File transfer started successfully! Transfer ID: ${result.transferId}`);

      // Start polling for progress
      pollTransferProgress(connectorId, result.transferId);
    } catch (err: any) {
      setError(`File transfer failed: ${err.message}`);
    }
  };

  const pollTransferProgress = async (connectorId: string, transferId: string) => {
    const pollInterval = setInterval(async () => {
      try {
        const response = await fetch(
          apiUrl(
            `sftp-connectors/${connectorId}/operations/transfer-results?transferId=${transferId}`
          ),
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ connectorId, transferId }),
          }
        );

        if (response.ok) {
          const result = await response.json();
          const transferResult = result.transferResults?.[0];

          if (transferResult) {
            setTransferProgress(prev => ({
              ...prev,
              [connectorId]: {
                ...prev[connectorId],
                status: transferResult.status,
                progress: transferResult.progress,
                endTime: transferResult.endTime,
              },
            }));

            if (transferResult.status === 'COMPLETED' || transferResult.status === 'FAILED') {
              clearInterval(pollInterval);
              // Add to history
              setTransferHistory(prev => ({
                ...prev,
                [connectorId]: [transferResult, ...(prev[connectorId] || [])],
              }));
            }
          }
        }
      } catch (err) {
        console.error('Error polling transfer progress:', err);
      }
    }, 2000); // Poll every 2 seconds

    // Stop polling after 5 minutes
    setTimeout(() => clearInterval(pollInterval), 300000);
  };

  const addTag = () => {
    // TODO: Replace with proper input dialog
    // const key = prompt('Enter tag key:');
    const key = 'default-key'; // Temporary fallback
    if (key) {
      // TODO: Replace with proper input dialog
      // const value = prompt('Enter tag value:');
      const value = 'default-value'; // Temporary fallback
      if (value !== null) {
        setForm(prev => ({
          ...prev,
          tags: { ...prev.tags, [key]: value },
        }));
      }
    }
  };

  const removeTag = (key: string) => {
    setForm(prev => {
      const newTags = { ...prev.tags };
      delete newTags[key];
      return { ...prev, tags: newTags };
    });
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setSuccess('Copied to clipboard!');
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      setError('Failed to copy to clipboard');
    }
  };

  if (loading && connectors.length === 0) {
    return (
      <div className='flex items-center justify-center p-8'>
        <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500'></div>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='flex items-center justify-between'>
        <div>
          <h2 className='text-xl font-semibold text-white'>SFTP Connectors</h2>
          <p className='text-slate-400'>Manage outbound SFTP connections for {companyName}</p>
        </div>
        <div className='flex items-center gap-3'>
          <Button
            onClick={() => (window.location.href = '/workflows')}
            className='bg-purple-600 hover:bg-purple-700 text-white'
            variant='soft'
          >
            <svg className='w-4 h-4 mr-2' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M13 10V3L4 14h7v7l9-11h-7z'
              />
            </svg>
            View All Workflows
          </Button>
          <Button
            onClick={() => setShowCreateModal(true)}
            className='bg-blue-600 hover:bg-blue-700 text-white'
          >
            <Plus className='w-4 h-4 mr-2' />
            New Connector
          </Button>
        </div>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className='p-4 bg-red-500/10 border border-red-500/20 rounded-lg'>
          <p className='text-red-400'>{error}</p>
        </div>
      )}
      {success && (
        <div className='p-4 bg-green-500/10 border border-green-500/20 rounded-lg'>
          <p className='text-green-400'>{success}</p>
        </div>
      )}

      {/* Connectors List */}
      {connectors.length === 0 ? (
        <Card className='p-8 text-center'>
          <Server className='w-12 h-12 mx-auto mb-4 text-slate-400' />
          <h3 className='text-lg font-medium text-white mb-2'>
            {!companyId || companyId === 'undefined'
              ? 'No SFTP Connectors Found'
              : 'No SFTP Connectors'}
          </h3>
          <p className='text-slate-400 mb-4'>
            {!companyId || companyId === 'undefined'
              ? 'No SFTP connectors have been created yet. Select a company to create connectors for that specific company.'
              : 'Create your first SFTP connector to enable outbound file transfers for this company'}
          </p>
          {companyId && companyId !== 'undefined' && (
            <Button
              onClick={() => setShowCreateModal(true)}
              className='bg-blue-600 hover:bg-blue-700 text-white'
            >
              <Plus className='w-4 h-4 mr-2' />
              Create Connector
            </Button>
          )}
        </Card>
      ) : (
        <div className='grid gap-4'>
          {connectors.map(connector => (
            <Card key={connector.connectorId} className='p-4'>
              <div className='flex items-center justify-between'>
                <div className='flex-1'>
                  <div className='flex items-center gap-3 mb-2'>
                    <h3 className='font-medium text-white'>{connector.name}</h3>
                    <span className='px-2 py-1 text-xs bg-slate-700 text-slate-300 rounded'>
                      {connector.authenticationType}
                    </span>
                    <div className='flex items-center gap-2'>
                      <span className='px-2 py-1 text-xs bg-green-600/20 text-green-400 rounded'>
                        {companies[connector.companyId] || connector.companyId || 'Unknown Company'}
                      </span>
                      <span className='px-2 py-1 text-xs bg-blue-600/20 text-blue-400 rounded'>
                        {connector.destinationType === 'INBOUND'
                          ? 'Inbound'
                          : connector.destinationType === 'OUTBOUND'
                            ? 'Outbound'
                            : companies[connector.companyId] ||
                              connector.companyId ||
                              'Custom Path'}
                      </span>
                    </div>
                  </div>
                  <div className='text-sm text-slate-400 space-y-1'>
                    <p>
                      Host: {connector.host}:{connector.port}
                    </p>
                    <p>Username: {connector.username}</p>
                    {connector.awsConnectorId && (
                      <div className='flex items-center gap-2'>
                        <p className='text-blue-400 font-mono text-xs'>
                          AWS ID: {connector.awsConnectorId}
                        </p>
                        <button
                          onClick={() => copyToClipboard(connector.awsConnectorId!)}
                          className='text-blue-400 hover:text-blue-300 transition-colors'
                          title='Copy AWS Connector ID'
                        >
                          <Copy className='w-3 h-3' />
                        </button>
                      </div>
                    )}
                    {connector.hostKey && (
                      <p className='text-green-400'>
                        Host Key: {connector.hostKey.substring(0, 20)}...
                      </p>
                    )}
                    <p>
                      Destination: {connector.s3Bucket}/{connector.s3Prefix}
                    </p>
                    <p>Created: {new Date(connector.createdAt).toLocaleDateString()}</p>
                  </div>
                </div>
                <div className='flex items-center gap-2'>
                  <Button
                    onClick={() =>
                      handleTestConnection(connector.awsConnectorId || connector.connectorId)
                    }
                    disabled={testingConnection === connector.connectorId}
                    className='bg-green-600 hover:bg-green-700 text-white'
                    variant='soft'
                  >
                    <TestTube className='w-4 h-4 mr-2' />
                    {testingConnection === connector.connectorId ? 'Testing...' : 'Test'}
                  </Button>
                  <Button
                    onClick={() => setShowFileBrowser(connector.connectorId)}
                    className='bg-indigo-600 hover:bg-indigo-700 text-white'
                    variant='soft'
                  >
                    <FolderOpen className='w-4 h-4 mr-2' />
                    Files
                  </Button>
                  <Button
                    onClick={() =>
                      (window.location.href = `/workflows?connectorId=${connector.connectorId}`)
                    }
                    className='bg-purple-600 hover:bg-purple-700 text-white'
                    variant='soft'
                  >
                    <svg
                      className='w-4 h-4 mr-2'
                      fill='none'
                      stroke='currentColor'
                      viewBox='0 0 24 24'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M13 10V3L4 14h7v7l9-11h-7z'
                      />
                    </svg>
                    Workflows
                  </Button>
                  <Button
                    onClick={() => populateFormForEdit(connector)}
                    className='bg-blue-600 hover:bg-blue-700 text-white'
                    variant='soft'
                  >
                    <Edit className='w-4 h-4 mr-2' />
                    Edit
                  </Button>
                  <Button
                    onClick={() => handleDelete(connector.connectorId)}
                    className='bg-red-600 hover:bg-red-700 text-white'
                    variant='soft'
                  >
                    <Trash2 className='w-4 h-4 mr-2' />
                    Delete
                  </Button>
                </div>
              </div>

              {/* File Operations Panel */}
              {showFileOperations === connector.connectorId && (
                <div className='mt-4 p-4 bg-slate-700/50 rounded-lg border border-slate-600'>
                  <div className='flex items-center justify-between mb-4'>
                    <h4 className='text-lg font-medium text-white'>File Operations</h4>
                    <Button
                      onClick={() => setShowFileOperations(null)}
                      className='bg-slate-600 hover:bg-slate-700 text-white'
                      variant='soft'
                    >
                      ×
                    </Button>
                  </div>

                  {/* Directory Listing */}
                  <div className='mb-4'>
                    <div className='flex items-center gap-2 mb-2'>
                      <Button
                        onClick={() => handleDirectoryListing(connector.connectorId, '/')}
                        disabled={loadingDirectory === connector.connectorId}
                        className='bg-blue-600 hover:bg-blue-700 text-white'
                        variant='soft'
                      >
                        <RefreshCw
                          className={`w-4 h-4 mr-2 ${loadingDirectory === connector.connectorId ? 'animate-spin' : ''}`}
                        />
                        List Directory
                      </Button>
                    </div>

                    {directoryListing[connector.connectorId] && (
                      <div className='bg-slate-800 rounded p-3 max-h-40 overflow-y-auto'>
                        <h5 className='text-sm font-medium text-slate-300 mb-2'>
                          Remote Directory Contents:
                        </h5>
                        <div className='space-y-1'>
                          {directoryListing[connector.connectorId].map(
                            (file: any, index: number) => (
                              <div key={index} className='flex items-center gap-2 text-sm'>
                                {file.type === 'directory' ? (
                                  <FolderOpen className='w-4 h-4 text-blue-400' />
                                ) : (
                                  <FileText className='w-4 h-4 text-slate-400' />
                                )}
                                <span className='text-slate-300'>{file.name}</span>
                                <span className='text-slate-500'>({file.size || 'N/A'} bytes)</span>
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Transfer Progress */}
                  {transferProgress[connector.connectorId] && (
                    <div className='mb-4 p-3 bg-slate-800 rounded'>
                      <div className='flex items-center gap-2 mb-2'>
                        <Clock className='w-4 h-4 text-blue-400' />
                        <span className='text-sm font-medium text-white'>Transfer Progress</span>
                        <span
                          className={`px-2 py-1 text-xs rounded ${
                            transferProgress[connector.connectorId].status === 'COMPLETED'
                              ? 'bg-green-600/20 text-green-400'
                              : transferProgress[connector.connectorId].status === 'FAILED'
                                ? 'bg-red-600/20 text-red-400'
                                : 'bg-blue-600/20 text-blue-400'
                          }`}
                        >
                          {transferProgress[connector.connectorId].status}
                        </span>
                      </div>
                      <div className='text-xs text-slate-400'>
                        <p>Transfer ID: {transferProgress[connector.connectorId].transferId}</p>
                        <p>Files: {transferProgress[connector.connectorId].files?.join(', ')}</p>
                        {transferProgress[connector.connectorId].progress && (
                          <p>Progress: {transferProgress[connector.connectorId].progress}%</p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Transfer History */}
                  {transferHistory[connector.connectorId] &&
                    transferHistory[connector.connectorId].length > 0 && (
                      <div className='mb-4'>
                        <h5 className='text-sm font-medium text-slate-300 mb-2'>
                          Recent Transfers:
                        </h5>
                        <div className='bg-slate-800 rounded p-3 max-h-32 overflow-y-auto'>
                          <div className='space-y-2'>
                            {transferHistory[connector.connectorId]
                              .slice(0, 5)
                              .map((transfer: any, index: number) => (
                                <div key={index} className='flex items-center gap-2 text-xs'>
                                  {transfer.status === 'COMPLETED' ? (
                                    <CheckCircle className='w-3 h-3 text-green-400' />
                                  ) : (
                                    <XCircle className='w-3 h-3 text-red-400' />
                                  )}
                                  <span className='text-slate-300'>{transfer.transferId}</span>
                                  <span className='text-slate-500'>
                                    {new Date(
                                      transfer.endTime || transfer.startTime
                                    ).toLocaleString()}
                                  </span>
                                </div>
                              ))}
                          </div>
                        </div>
                      </div>
                    )}

                  {/* Quick Actions */}
                  <div className='flex gap-2'>
                    <Button
                      onClick={() => {
                        // TODO: Replace with proper input dialog
                        // const files = prompt('Enter file paths to download (comma-separated):');
                        const files = 'test-file.txt'; // Temporary fallback
                        if (files) {
                          handleFileTransfer(
                            connector.connectorId,
                            'download',
                            files.split(',').map(f => f.trim())
                          );
                        }
                      }}
                      className='bg-green-600 hover:bg-green-700 text-white'
                      variant='soft'
                    >
                      <Download className='w-4 h-4 mr-2' />
                      Download Files
                    </Button>
                    <Button
                      onClick={() => {
                        // TODO: Replace with proper input dialog
                        // const files = prompt('Enter file paths to upload (comma-separated):');
                        const files = 'test-file.txt'; // Temporary fallback
                        if (files) {
                          handleFileTransfer(
                            connector.connectorId,
                            'upload',
                            files.split(',').map(f => f.trim())
                          );
                        }
                      }}
                      className='bg-orange-600 hover:bg-orange-700 text-white'
                      variant='soft'
                    >
                      <Upload className='w-4 h-4 mr-2' />
                      Upload Files
                    </Button>
                  </div>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* Create Connector Modal */}
      {showCreateModal && (
        <div className='fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50'>
          <div className='bg-slate-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto'>
            <div className='p-6 border-b border-slate-700'>
              <h3 className='text-lg font-medium text-white'>Create SFTP Connector</h3>
              <p className='text-slate-400'>Configure outbound SFTP connection for {companyName}</p>
            </div>

            <form onSubmit={handleSubmit} className='p-6 space-y-6'>
              {/* Basic Information */}
              <div className='grid grid-cols-2 gap-4'>
                <div>
                  <label
                    htmlFor='connector-name'
                    className='block text-sm font-medium text-white mb-2'
                  >
                    Connector Name
                  </label>
                  <input
                    id='connector-name'
                    type='text'
                    value={form.name}
                    onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
                    className='w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white'
                    required
                  />
                </div>
                <div>
                  <label
                    htmlFor='connector-host'
                    className='block text-sm font-medium text-white mb-2'
                  >
                    Host
                  </label>
                  <input
                    id='connector-host'
                    type='text'
                    value={form.host}
                    onChange={e => setForm(prev => ({ ...prev, host: e.target.value }))}
                    className='w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white'
                    placeholder='sftp.example.com'
                    required
                  />
                </div>
              </div>

              <div className='grid grid-cols-2 gap-4'>
                <div>
                  <label
                    htmlFor='connector-port'
                    className='block text-sm font-medium text-white mb-2'
                  >
                    Port
                  </label>
                  <input
                    id='connector-port'
                    type='number'
                    value={form.port}
                    onChange={e => setForm(prev => ({ ...prev, port: parseInt(e.target.value) }))}
                    className='w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white'
                    min='1'
                    max='65535'
                    required
                  />
                </div>
                <div>
                  <label
                    htmlFor='connector-username'
                    className='block text-sm font-medium text-white mb-2'
                  >
                    Username
                  </label>
                  <input
                    id='connector-username'
                    type='text'
                    value={form.username}
                    onChange={e => setForm(prev => ({ ...prev, username: e.target.value }))}
                    className='w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white'
                    required
                  />
                </div>
              </div>

              {/* Authentication */}
              <div>
                <label className='block text-sm font-medium text-white mb-2'>
                  Authentication Type
                </label>
                <div className='flex gap-4'>
                  <label className='flex items-center p-3 border border-white/10 rounded-lg cursor-pointer hover:bg-white/5 transition-colors'>
                    <input
                      type='radio'
                      name='authType'
                      value='password'
                      checked={form.authenticationType === 'password'}
                      onChange={e =>
                        setForm(prev => ({
                          ...prev,
                          authenticationType: e.target.value as 'password' | 'ssh-key',
                        }))
                      }
                      className='mr-3'
                    />
                    <div>
                      <div className='font-medium text-white'>Password</div>
                      <div className='text-xs text-slate-400'>
                        Use username and password authentication
                      </div>
                    </div>
                  </label>
                  <label className='flex items-center p-3 border border-white/10 rounded-lg cursor-pointer hover:bg-white/5 transition-colors'>
                    <input
                      type='radio'
                      name='authType'
                      value='ssh-key'
                      checked={form.authenticationType === 'ssh-key'}
                      onChange={e =>
                        setForm(prev => ({
                          ...prev,
                          authenticationType: e.target.value as 'password' | 'ssh-key',
                        }))
                      }
                      className='mr-3'
                    />
                    <div>
                      <div className='font-medium text-white'>SSH Key</div>
                      <div className='text-xs text-slate-400'>
                        Use SSH private key authentication
                      </div>
                    </div>
                  </label>
                </div>
              </div>

              {/* Credentials */}
              {form.authenticationType === 'password' ? (
                <div>
                  <label
                    htmlFor='connector-password'
                    className='block text-sm font-medium text-white mb-2'
                  >
                    Password
                  </label>
                  <input
                    id='connector-password'
                    type='password'
                    value={form.password}
                    onChange={e => setForm(prev => ({ ...prev, password: e.target.value }))}
                    className='w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white'
                    required
                  />
                </div>
              ) : (
                <div>
                  <label className='block text-sm font-medium text-white mb-2'>
                    SSH Private Key
                  </label>
                  <textarea
                    value={form.sshPrivateKey}
                    onChange={e => setForm(prev => ({ ...prev, sshPrivateKey: e.target.value }))}
                    className='w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white h-32 font-mono text-sm'
                    placeholder='-----BEGIN OPENSSH PRIVATE KEY-----'
                    required
                  />
                </div>
              )}

              {/* Workflow Type */}
              <div>
                <label className='block text-sm font-medium text-white mb-2'>Workflow Type</label>
                <div className='flex gap-4'>
                  <label className='flex items-center p-3 border border-white/10 rounded-lg cursor-pointer hover:bg-white/5 transition-colors'>
                    <input
                      type='radio'
                      name='workflowType'
                      value='INBOUND'
                      checked={form.workflowType === 'INBOUND'}
                      onChange={e => {
                        const newWorkflowType = e.target.value as 'INBOUND' | 'OUTBOUND';
                        setForm(prev => ({
                          ...prev,
                          workflowType: newWorkflowType,
                          destinationType: newWorkflowType, // Auto-select destination type
                        }));
                      }}
                      className='mr-3'
                    />
                    <div>
                      <div className='font-medium text-white'>Inbound</div>
                      <div className='text-xs text-slate-400'>
                        Files coming FROM external SFTP TO company
                      </div>
                    </div>
                  </label>
                  <label className='flex items-center p-3 border border-white/10 rounded-lg cursor-pointer hover:bg-white/5 transition-colors'>
                    <input
                      type='radio'
                      name='workflowType'
                      value='OUTBOUND'
                      checked={form.workflowType === 'OUTBOUND'}
                      onChange={e => {
                        const newWorkflowType = e.target.value as 'INBOUND' | 'OUTBOUND';
                        setForm(prev => ({
                          ...prev,
                          workflowType: newWorkflowType,
                          destinationType: newWorkflowType, // Auto-select destination type
                        }));
                      }}
                      className='mr-3'
                    />
                    <div>
                      <div className='font-medium text-white'>Outbound</div>
                      <div className='text-xs text-slate-400'>
                        Files going FROM company TO external SFTP
                      </div>
                    </div>
                  </label>
                </div>
              </div>

              {/* S3 Destination */}
              <div>
                <label className='block text-sm font-medium text-white mb-2'>S3 Destination</label>
                <div className='space-y-3'>
                  <div>
                    <label
                      htmlFor='s3-bucket-create'
                      className='block text-sm font-medium text-white mb-2'
                    >
                      S3 Bucket
                    </label>
                    <select
                      id='s3-bucket-create'
                      value={form.s3Bucket}
                      onChange={e => setForm(prev => ({ ...prev, s3Bucket: e.target.value }))}
                      className='w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white'
                      aria-label='Select S3 bucket for file storage'
                      required
                    >
                      <option value='ohit-dev-transfer-storage'>
                        ohit-dev-transfer-storage (Primary - Company Files)
                      </option>
                      <option value='ohit-dev-transfer-log'>ohit-dev-transfer-log (Logs)</option>
                    </select>
                    {form.s3Bucket === 'ohit-dev-transfer-storage' && (
                      <div className='mt-2 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg'>
                        <div className='flex items-center gap-2 text-amber-400'>
                          <AlertTriangle className='w-4 h-4' />
                          <span className='text-sm'>
                            This bucket is used for company files. Ensure proper access controls.
                          </span>
                        </div>
                      </div>
                    )}
                  </div>

                  <div>
                    <label className='block text-sm font-medium text-white mb-2'>
                      Destination Type
                    </label>
                    <div className='flex gap-4'>
                      <label className='flex items-center p-3 border border-white/10 rounded-lg cursor-pointer hover:bg-white/5 transition-colors'>
                        <input
                          type='radio'
                          name='folderType'
                          value='INBOUND'
                          checked={form.destinationType === 'INBOUND'}
                          onChange={e =>
                            handleDestinationTypeChange(
                              e.target.value as 'INBOUND' | 'OUTBOUND' | 'CUSTOM_PATH'
                            )
                          }
                          className='mr-3'
                        />
                        <div>
                          <div className='font-medium text-white'>Inbound</div>
                          <div className='text-xs text-slate-400'>
                            Files stored in company-specific folder for inbound transfers
                          </div>
                        </div>
                      </label>
                      <label className='flex items-center p-3 border border-white/10 rounded-lg cursor-pointer hover:bg-white/5 transition-colors'>
                        <input
                          type='radio'
                          name='folderType'
                          value='OUTBOUND'
                          checked={form.destinationType === 'OUTBOUND'}
                          onChange={e =>
                            handleDestinationTypeChange(
                              e.target.value as 'INBOUND' | 'OUTBOUND' | 'CUSTOM_PATH'
                            )
                          }
                          className='mr-3'
                        />
                        <div>
                          <div className='font-medium text-white'>Outbound</div>
                          <div className='text-xs text-slate-400'>
                            Files stored in bucket root for outbound transfers
                          </div>
                        </div>
                      </label>
                      <label className='flex items-center p-3 border border-white/10 rounded-lg cursor-pointer hover:bg-white/5 transition-colors'>
                        <input
                          type='radio'
                          name='folderType'
                          value='CUSTOM_PATH'
                          checked={form.destinationType === 'CUSTOM_PATH'}
                          onChange={e =>
                            handleDestinationTypeChange(
                              e.target.value as 'INBOUND' | 'OUTBOUND' | 'CUSTOM_PATH'
                            )
                          }
                          className='mr-3'
                        />
                        <div>
                          <div className='font-medium text-white'>Custom Path</div>
                          <div className='text-xs text-slate-400'>
                            Files stored in a specific path within the bucket
                          </div>
                        </div>
                      </label>
                    </div>
                  </div>

                  {form.destinationType === 'CUSTOM_PATH' && (
                    <div>
                      <label className='block text-sm font-medium text-white mb-2'>
                        Custom S3 Prefix (Optional)
                      </label>
                      <input
                        type='text'
                        value={form.s3Prefix}
                        onChange={e => setForm(prev => ({ ...prev, s3Prefix: e.target.value }))}
                        className='w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white'
                        placeholder='custom/path/'
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Auto-Push Checkbox */}
              <div className='mb-4'>
                <label className='flex items-center gap-3 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg'>
                  <input
                    type='checkbox'
                    checked={form.enableAutoPush}
                    disabled={form.workflowType === 'INBOUND'}
                    onChange={e => setForm(prev => ({ ...prev, enableAutoPush: e.target.checked }))}
                    className='mr-3'
                  />
                  <div>
                    <div className='font-medium text-blue-300'>
                      Automatic Outbound Workflow Push
                    </div>
                    <div className='text-xs text-blue-200'>
                      Automatically push files to SFTP connector using intelligent routing.
                    </div>
                  </div>
                </label>
              </div>

              {/* Auto-push configuration fields */}
              {form.enableAutoPush && (
                <div className='space-y-4 p-4 bg-blue-500/5 border border-blue-500/10 rounded-lg mb-4'>
                  <div>
                    <label className='block text-sm font-medium text-white mb-2'>
                      SFTP Remote Directory Path
                    </label>
                    <input
                      type='text'
                      value={form.homeDirectoryPath}
                      onChange={e =>
                        setForm(prev => ({ ...prev, homeDirectoryPath: e.target.value }))
                      }
                      placeholder='/home'
                      className='w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500'
                    />
                    <div className='text-xs text-gray-400 mt-1'>
                      The remote directory on the SFTP server where files will be pushed (e.g.,
                      'upload')
                    </div>
                  </div>

                  <div>
                    <label className='block text-sm font-medium text-white mb-2'>
                      Notification Email
                    </label>
                    <input
                      type='email'
                      value={form.notificationEmail}
                      onChange={e =>
                        setForm(prev => ({ ...prev, notificationEmail: e.target.value }))
                      }
                      placeholder='user@example.com'
                      className='w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500'
                    />
                    <div className='text-xs text-gray-400 mt-1'>
                      Email address for success/failure notifications
                    </div>
                  </div>
                </div>
              )}

              {/* Tags */}
              <div>
                <div className='flex items-center justify-between mb-2'>
                  <label className='block text-sm font-medium text-white'>Tags</label>
                  <Button
                    type='button'
                    onClick={addTag}
                    className='bg-slate-600 hover:bg-slate-700 text-white'
                    variant='soft'
                  >
                    Add Tag
                  </Button>
                </div>
                <div className='space-y-2'>
                  {Object.entries(form.tags).map(([key, value]) => (
                    <div key={key} className='flex items-center gap-2'>
                      <span className='px-2 py-1 bg-slate-700 text-slate-300 rounded text-sm'>
                        {key}: {value}
                      </span>
                      <Button
                        type='button'
                        onClick={() => removeTag(key)}
                        className='bg-red-600 hover:bg-red-700 text-white p-1'
                        variant='soft'
                      >
                        <Trash2 className='w-3 h-3' />
                      </Button>
                    </div>
                  ))}
                  {Object.keys(form.tags).length === 0 && (
                    <p className='text-slate-400 text-sm'>No tags added</p>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className='pt-4 border-t border-slate-700'>
                <div className='mb-3 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg'>
                  <p className='text-sm text-blue-300'>
                    💡 <strong>Next Steps:</strong> After creating the connector, you'll need to
                    attach it to a Transfer Family server to enable file transfers.
                  </p>
                </div>
                <div className='flex items-center justify-end gap-3'>
                  <Button
                    type='button'
                    onClick={() => {
                      setShowCreateModal(false);
                      resetForm();
                    }}
                    className='bg-slate-600 hover:bg-slate-700 text-white'
                    variant='soft'
                  >
                    Cancel
                  </Button>
                  <Button
                    type='submit'
                    disabled={loading}
                    className='bg-green-600 hover:bg-green-700 text-white'
                  >
                    {loading ? 'Creating...' : 'Save & Attach to Server'}
                  </Button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Connector Modal */}
      {showEditModal && editingConnector && (
        <div className='fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50'>
          <div className='bg-slate-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto'>
            <div className='p-6 border-b border-slate-700'>
              <h3 className='text-lg font-medium text-white'>Edit SFTP Connector</h3>
              <p className='text-slate-400'>Update connector: {editingConnector.name}</p>
            </div>

            <form onSubmit={handleEditConnector} className='p-6 space-y-6'>
              {/* Same form fields as create, but with current values */}
              <div className='grid grid-cols-2 gap-4'>
                <div>
                  <label
                    htmlFor='edit-connector-name'
                    className='block text-sm font-medium text-white mb-2'
                  >
                    Connector Name
                  </label>
                  <input
                    id='edit-connector-name'
                    type='text'
                    value={form.name}
                    onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
                    className='w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white'
                    required
                  />
                </div>
                <div>
                  <label htmlFor='edit-host' className='block text-sm font-medium text-white mb-2'>
                    Host
                  </label>
                  <input
                    id='edit-host'
                    type='text'
                    value={form.host}
                    onChange={e => setForm(prev => ({ ...prev, host: e.target.value }))}
                    className='w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white'
                    required
                  />
                </div>
              </div>

              <div className='grid grid-cols-2 gap-4'>
                <div>
                  <label htmlFor='edit-port' className='block text-sm font-medium text-white mb-2'>
                    Port
                  </label>
                  <input
                    id='edit-port'
                    type='number'
                    value={form.port}
                    onChange={e => setForm(prev => ({ ...prev, port: parseInt(e.target.value) }))}
                    className='w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white'
                    min='1'
                    max='65535'
                    required
                  />
                </div>
                <div>
                  <label
                    htmlFor='edit-username'
                    className='block text-sm font-medium text-white mb-2'
                  >
                    Username
                  </label>
                  <input
                    id='edit-username'
                    type='text'
                    value={form.username}
                    onChange={e => setForm(prev => ({ ...prev, username: e.target.value }))}
                    className='w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white'
                    required
                  />
                </div>
              </div>

              {/* Authentication */}
              <div>
                <label className='block text-sm font-medium text-white mb-2'>
                  Authentication Type
                </label>
                <div className='flex gap-4'>
                  <label className='flex items-center p-3 border border-white/10 rounded-lg cursor-pointer hover:bg-white/5 transition-colors'>
                    <input
                      type='radio'
                      name='editAuthType'
                      value='password'
                      checked={form.authenticationType === 'password'}
                      onChange={e =>
                        setForm(prev => ({
                          ...prev,
                          authenticationType: e.target.value as 'password' | 'ssh-key',
                        }))
                      }
                      className='mr-3'
                    />
                    <div>
                      <div className='font-medium text-white'>Password</div>
                      <div className='text-xs text-slate-400'>
                        Use username and password authentication
                      </div>
                    </div>
                  </label>
                  <label className='flex items-center p-3 border border-white/10 rounded-lg cursor-pointer hover:bg-white/5 transition-colors'>
                    <input
                      type='radio'
                      name='editAuthType'
                      value='ssh-key'
                      checked={form.authenticationType === 'ssh-key'}
                      onChange={e =>
                        setForm(prev => ({
                          ...prev,
                          authenticationType: e.target.value as 'password' | 'ssh-key',
                        }))
                      }
                      className='mr-3'
                    />
                    <div>
                      <div className='font-medium text-white'>SSH Key</div>
                      <div className='text-xs text-slate-400'>
                        Use SSH private key authentication
                      </div>
                    </div>
                  </label>
                </div>
              </div>

              {/* Credentials */}
              {form.authenticationType === 'password' ? (
                <div>
                  <label className='block text-sm font-medium text-white mb-2'>
                    Password (leave blank to keep current)
                  </label>
                  <input
                    type='password'
                    value={form.password}
                    onChange={e => setForm(prev => ({ ...prev, password: e.target.value }))}
                    className='w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white'
                    placeholder='Enter new password or leave blank'
                  />
                </div>
              ) : (
                <div>
                  <label className='block text-sm font-medium text-white mb-2'>
                    SSH Private Key (leave blank to keep current)
                  </label>
                  <textarea
                    value={form.sshPrivateKey}
                    onChange={e => setForm(prev => ({ ...prev, sshPrivateKey: e.target.value }))}
                    className='w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white h-32 font-mono text-sm'
                    placeholder='Enter new private key or leave blank to keep current'
                  />
                </div>
              )}

              {/* Workflow Type */}
              <div>
                <label className='block text-sm font-medium text-white mb-2'>Workflow Type</label>
                <div className='flex gap-4'>
                  <label className='flex items-center p-3 border border-white/10 rounded-lg cursor-pointer hover:bg-white/5 transition-colors'>
                    <input
                      type='radio'
                      name='editWorkflowType'
                      value='INBOUND'
                      checked={form.workflowType === 'INBOUND'}
                      onChange={e =>
                        setForm(prev => ({
                          ...prev,
                          workflowType: e.target.value as 'INBOUND' | 'OUTBOUND',
                        }))
                      }
                      className='mr-3'
                    />
                    <div>
                      <div className='font-medium text-white'>Inbound</div>
                      <div className='text-xs text-slate-400'>
                        Files coming FROM external SFTP TO company
                      </div>
                    </div>
                  </label>
                  <label className='flex items-center p-3 border border-white/10 rounded-lg cursor-pointer hover:bg-white/5 transition-colors'>
                    <input
                      type='radio'
                      name='editWorkflowType'
                      value='OUTBOUND'
                      checked={form.workflowType === 'OUTBOUND'}
                      onChange={e =>
                        setForm(prev => ({
                          ...prev,
                          workflowType: e.target.value as 'INBOUND' | 'OUTBOUND',
                        }))
                      }
                      className='mr-3'
                    />
                    <div>
                      <div className='font-medium text-white'>Outbound</div>
                      <div className='text-xs text-slate-400'>
                        Files going FROM company TO external SFTP
                      </div>
                    </div>
                  </label>
                </div>
              </div>

              {/* S3 Destination */}
              <div>
                <label className='block text-sm font-medium text-white mb-2'>S3 Destination</label>
                <div className='space-y-3'>
                  <div>
                    <label
                      htmlFor='s3-bucket-edit'
                      className='block text-sm font-medium text-white mb-2'
                    >
                      S3 Bucket
                    </label>
                    <select
                      id='s3-bucket-edit'
                      value={form.s3Bucket}
                      onChange={e => setForm(prev => ({ ...prev, s3Bucket: e.target.value }))}
                      className='w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white'
                      aria-label='Select S3 bucket for file storage'
                      required
                    >
                      <option value='ohit-dev-transfer-storage'>
                        ohit-dev-transfer-storage (Primary - Company Files)
                      </option>
                      <option value='ohit-dev-transfer-log'>ohit-dev-transfer-log (Logs)</option>
                    </select>
                  </div>

                  <div>
                    <label className='block text-sm font-medium text-white mb-2'>
                      Destination Type
                    </label>
                    <div className='flex gap-4'>
                      <label className='flex items-center p-3 border border-white/10 rounded-lg cursor-pointer hover:bg-white/5 transition-colors'>
                        <input
                          type='radio'
                          name='editFolderType'
                          value='INBOUND'
                          checked={form.destinationType === 'INBOUND'}
                          onChange={e =>
                            handleDestinationTypeChange(
                              e.target.value as 'INBOUND' | 'OUTBOUND' | 'CUSTOM_PATH'
                            )
                          }
                          className='mr-3'
                        />
                        <div>
                          <div className='font-medium text-white'>Inbound</div>
                          <div className='text-xs text-slate-400'>
                            Files stored in company-specific folder for inbound transfers
                          </div>
                        </div>
                      </label>
                      <label className='flex items-center p-3 border border-white/10 rounded-lg cursor-pointer hover:bg-white/5 transition-colors'>
                        <input
                          type='radio'
                          name='editFolderType'
                          value='OUTBOUND'
                          checked={form.destinationType === 'OUTBOUND'}
                          onChange={e =>
                            handleDestinationTypeChange(
                              e.target.value as 'INBOUND' | 'OUTBOUND' | 'CUSTOM_PATH'
                            )
                          }
                          className='mr-3'
                        />
                        <div>
                          <div className='font-medium text-white'>Outbound</div>
                          <div className='text-xs text-slate-400'>
                            Files stored in bucket root for outbound transfers
                          </div>
                        </div>
                      </label>
                      <label className='flex items-center p-3 border border-white/10 rounded-lg cursor-pointer hover:bg-white/5 transition-colors'>
                        <input
                          type='radio'
                          name='editFolderType'
                          value='CUSTOM_PATH'
                          checked={form.destinationType === 'CUSTOM_PATH'}
                          onChange={e =>
                            handleDestinationTypeChange(
                              e.target.value as 'INBOUND' | 'OUTBOUND' | 'CUSTOM_PATH'
                            )
                          }
                          className='mr-3'
                        />
                        <div>
                          <div className='font-medium text-white'>Custom Path</div>
                          <div className='text-xs text-slate-400'>
                            Files stored in a specific path within the bucket
                          </div>
                        </div>
                      </label>
                    </div>
                  </div>

                  {form.destinationType === 'CUSTOM_PATH' && (
                    <div>
                      <label className='block text-sm font-medium text-white mb-2'>
                        Custom S3 Prefix (Optional)
                      </label>
                      <input
                        type='text'
                        value={form.s3Prefix}
                        onChange={e => setForm(prev => ({ ...prev, s3Prefix: e.target.value }))}
                        className='w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white'
                        placeholder='custom/path/'
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Auto-Push Checkbox */}
              <div className='mb-4'>
                <label className='flex items-center gap-3 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg'>
                  <input
                    type='checkbox'
                    checked={form.enableAutoPush}
                    disabled={form.workflowType === 'INBOUND'}
                    onChange={e => setForm(prev => ({ ...prev, enableAutoPush: e.target.checked }))}
                    className='mr-3'
                  />
                  <div>
                    <div className='font-medium text-blue-300'>
                      Automatic Outbound Workflow Push
                    </div>
                    <div className='text-xs text-blue-200'>
                      Automatically push files to SFTP connector using intelligent routing.
                    </div>
                  </div>
                </label>
              </div>

              {/* Auto-push configuration fields */}
              {form.enableAutoPush && (
                <div className='space-y-4 p-4 bg-blue-500/5 border border-blue-500/10 rounded-lg mb-4'>
                  <div>
                    <label className='block text-sm font-medium text-white mb-2'>
                      SFTP Remote Directory Path
                    </label>
                    <input
                      type='text'
                      value={form.homeDirectoryPath}
                      onChange={e =>
                        setForm(prev => ({ ...prev, homeDirectoryPath: e.target.value }))
                      }
                      placeholder='/home'
                      className='w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500'
                    />
                    <div className='text-xs text-gray-400 mt-1'>
                      The remote directory on the SFTP server where files will be pushed (e.g.,
                      'upload')
                    </div>
                  </div>

                  <div>
                    <label className='block text-sm font-medium text-white mb-2'>
                      Notification Email
                    </label>
                    <input
                      type='email'
                      value={form.notificationEmail}
                      onChange={e =>
                        setForm(prev => ({ ...prev, notificationEmail: e.target.value }))
                      }
                      placeholder='user@example.com'
                      className='w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500'
                    />
                    <div className='text-xs text-gray-400 mt-1'>
                      Email address for success/failure notifications
                    </div>
                  </div>
                </div>
              )}

              {/* Tags */}
              <div>
                <div className='flex items-center justify-between mb-2'>
                  <label className='block text-sm font-medium text-white'>Tags</label>
                  <Button
                    type='button'
                    onClick={addTag}
                    className='bg-slate-600 hover:bg-slate-700 text-white'
                    variant='soft'
                  >
                    Add Tag
                  </Button>
                </div>
                <div className='space-y-2'>
                  {Object.entries(form.tags).map(([key, value]) => (
                    <div key={key} className='flex items-center gap-2'>
                      <span className='px-2 py-1 bg-slate-700 text-slate-300 rounded text-sm'>
                        {key}: {value}
                      </span>
                      <Button
                        type='button'
                        onClick={() => removeTag(key)}
                        className='bg-red-600 hover:bg-red-700 text-white p-1'
                        variant='soft'
                      >
                        <Trash2 className='w-3 h-3' />
                      </Button>
                    </div>
                  ))}
                  {Object.keys(form.tags).length === 0 && (
                    <p className='text-slate-400 text-sm'>No tags added</p>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className='pt-4 border-t border-slate-700'>
                <div className='flex items-center justify-end gap-3'>
                  <Button
                    type='button'
                    onClick={() => {
                      setShowEditModal(false);
                      setEditingConnector(null);
                      resetForm();
                    }}
                    className='bg-slate-600 hover:bg-slate-700 text-white'
                    variant='soft'
                  >
                    Cancel
                  </Button>
                  <Button
                    type='submit'
                    disabled={loading}
                    className='bg-blue-600 hover:bg-blue-700 text-white'
                  >
                    {loading ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Dual Pane File Browser */}
      {showFileBrowser && (
        <DualPaneFileBrowser
          connectorId={showFileBrowser}
          connectorName={connectors.find(c => c.connectorId === showFileBrowser)?.name || 'Unknown'}
          companyId={companyId || ''}
          s3Bucket={
            connectors.find(c => c.connectorId === showFileBrowser)?.s3Bucket ||
            'ohit-dev-transfer-storage'
          }
          s3Prefix={connectors.find(c => c.connectorId === showFileBrowser)?.s3Prefix || ''}
          onClose={() => setShowFileBrowser(null)}
        />
      )}
    </div>
  );
}
